/**
 * StateFinancing — /states/financing
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { RefreshCw, DollarSign } from "lucide-react";
import client from "@/api/client";
import { PageHeader, SectionCard, MetricCard, DataTable, CTAButton } from "@/components/ds";

const STAGE_COLORS = {
  screening:        "bg-slate-100 text-slate-600",
  data_request:     "bg-blue-100 text-blue-700",
  due_diligence:    "bg-amber-100 text-amber-700",
  credit_committee: "bg-orange-100 text-orange-700",
  term_sheet:       "bg-teal-100 text-teal-700",
  financial_close:  "bg-emerald-100 text-emerald-700",
  rejected:         "bg-red-100 text-red-600",
};

export default function StateFinancing() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { stateId, geography: geo, stateParams } = useStateContext();

  useEffect(() => {
    client.get(`/states/financing-pipeline${stateParams}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [geo]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const deals = data?.deals || [];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="Financing & Investment Pipeline"
        subtitle={`${geo} · ${data?.total_deals || 0} active deals`}
        backUrl="/states/overview"
        backLabel="State Overview"
      />

      <div className="grid grid-cols-3 gap-4">
        <MetricCard
          label="Active Deals"
          value={(data?.total_deals || 0).toString()}
          sub="In pipeline"
          color="text-slate-900"
        />
        <MetricCard
          label="CAPEX Pipeline"
          value={`$${((data?.total_capex_pipeline_usd || 0) / 1e6).toFixed(1)}M`}
          sub="Total indicative"
          color="text-teal-700"
        />
        <MetricCard
          label="Reduction Potential"
          value={`${((data?.total_reduction_pipeline_tco2e || 0) / 1000).toFixed(0)}kt`}
          unit="tCO₂e"
          sub="Pipeline estimate"
          color="text-emerald-700"
        />
      </div>

      {deals.length === 0 ? (
        <SectionCard>
          <div className="py-10 text-center">
            <DollarSign size={28} className="text-slate-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-600">No active deals found for {geo}</p>
            <p className="text-xs text-slate-400 mt-1 mb-4">Submit a state programme to create FI pipeline deals.</p>
            <div className="flex gap-3 justify-center">
              <CTAButton variant="action" onClick={() => navigate("/states/programmes")}>
                Go to Programmes
              </CTAButton>
              <CTAButton variant="secondary" onClick={() => navigate("/fi")}>
                Go to FI Workspace
              </CTAButton>
            </div>
          </div>
        </SectionCard>
      ) : (
        <DataTable
          columns={[
            { label: "Deal" },
            { label: "Project" },
            { label: "Organization" },
            { label: "Sector" },
            { label: "Stage" },
            { label: "Reduction", align: "right" },
          ]}
        >
          {deals.map((d) => (
            <tr key={d.deal_id} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3 font-mono text-xs text-slate-500">{d.deal_id}</td>
              <td className="px-4 py-3 font-semibold text-sm text-slate-900">{d.project}</td>
              <td className="px-4 py-3 text-sm text-slate-500">{d.org}</td>
              <td className="px-4 py-3 text-sm text-slate-500 capitalize">{d.sector || "—"}</td>
              <td className="px-4 py-3">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STAGE_COLORS[d.stage] || STAGE_COLORS.screening}`}>
                  {d.stage?.replace(/_/g, " ")}
                </span>
              </td>
              <td className="px-4 py-3 text-right font-semibold text-sm text-teal-700 tabular-nums">
                {(d.reduction_tco2e || 0).toFixed(0)} t
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}
